/* eslint-disable react/react-in-jsx-scope */
import CardProject from "@/components/card-v2/CardProject";
import ErrorBoundary from "@/components/errors/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { convertToEuro } from "@/libs/amount";
import { calculateFundingTotals, calculReportingProject } from "@/libs/utils";
import { Devise } from "@/types/pp";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useMatch } from "react-router-dom";
import ChartItemGlobal from "./ChartItemGlobal";
import MapClusterPrograms from "./MapClusterPrograms";
import StatCardColoredProgram from "./StatCardColoredProgram";
import { Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Indicator } from "@/types/dashboard";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/auth/hooks/useAuth";
import { useMutateData } from "@/hooks/useMutation";
import { toast } from "@/components/ui/use-toast";

const ProjectListCard = ({ projects, actions }) => {
  const [searchParams] = useSearchParams();
  const { firstname, idPp, programId } = useParams();

  const queryParams = useMemo(() => {
    const q = new URLSearchParams();
    q.set("scope", "financer");

    // PRIORITÉ: programId puis idPp, sinon all
    if (programId) {
      q.set("mode", "programProjects");
      q.set("programId", String(programId));
      return q;
    }

    if (idPp) {
      q.set("mode", "carrierProjects");
      q.set("carrierId", String(idPp));
      return q;
    }

    q.set("mode", "allProjects");
    return q;
  }, [idPp, programId]);

  const carrier = searchParams.get("carrier");
  const [devises, setDevises] = useState<Devise[]>([]);
  const [totalFinancement, setTotalFinancement] = useState(0);
  const [totalSuivi, setTotalSuivi] = useState(0);
  const [showWarningFinancement, setShowWarningFinancement] = useState(false);
  const [valueCO, setValueCO] = useState(0);
  const [valueAC, setValueAC] = useState(0);
  const [projectSelected, setProjectSelected] = useState<
    { name: string; id: number }[]
  >([]);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isSheetOpenInd, setIsSheetOpenInd] = useState(false);
  const { user } = useAuth();

  const effectiveEndpoint = !isSheetOpenInd
    ? ""
    : `${import.meta.env.VITE_API_ENDPOINT}/getting/financers/${user.id
    }/indicators?${queryParams.toString()}`;

  const {
    data: indicators,
    loading: indicatorLoading,
    isFetching,
  } = useFetchDataV2<Indicator[]>(
    effectiveEndpoint,
    `indicators-${user?.id}`,
    1000 * 60 * 10
  );

  const isChecked = ({ name, id }: { name: string; id: number }) =>
    projectSelected.some(
      (project) => project.name === name && project.id === id
    );

  const { data } = useFetchDataV2(
    `${import.meta.env.VITE_API_ENDPOINT}/report/programs?context=program&${queryParams.toString()}`,
    `reportDataDash`,
    1000 * 60 * 60
  );


  const {
    data: dataDevises,
    loading: loadingDevises,
    error: errorDevises,
  } = useFetchDataV2<Devise[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/devises`,
    "devises"
  );

  useEffect(() => {
    if (
      dataDevises &&
      dataDevises.length > 0 &&
      !loadingDevises &&
      !errorDevises
    ) {
      setDevises(dataDevises);
    }
  }, [dataDevises]);

  const handleChangeProjectSelected = ({
    name,
    id,
  }: {
    name: string;
    id: number;
  }) => {
    if (name === "all") {
      if (projects.length === projectSelected.length) {
        // Si tous les projets sont déjà sélectionnés, on les désélectionne tous
        setProjectSelected([]);
      } else {
        // Sinon, on sélectionne tous les projets
        setProjectSelected(
          projects?.map((project) => ({
            name: project.name,
            id: project.id,
          }))
        );
      }
    } else {
      // Pour chaque projet, on vérifie si il est déjà sélectionné
      setProjectSelected((prev) => {
        return prev.some((item) => item.id === id)
          ? prev.filter((item) => item.id !== id) // Si déjà sélectionné, on le retire
          : [...prev, { name: name, id: id }]; // Sinon, on l'ajoute
      });
    }
  };

  const { mutate: updateParamsIndicator } = useMutateData({
    method: "POST",
    id: "programs",
    onSuccess: (data) => {
      toast({
        title: "Paramètres sauvegardés",
        description: "Les changements ont été enregistrés avec succès",
      });
    },
  });

  const handleChangeParamsIndicator = async (indicator, newValue) => {
    const payload = {
      currentItem: {
        id: indicator.id,
        name: indicator.name,
      },
      cumulative: Boolean(indicator.cumulative),
      graphe: Boolean(indicator.graphe),
      dashboard: Boolean(indicator.dashboard),
      programme: Boolean(newValue),
    };
    updateParamsIndicator({
      endpoint: `${import.meta.env.VITE_API_ENDPOINT
        }/parameter-indicators/crupdate`,
      body: payload,
    });
  };

  const dataReportingV2 = useMemo(() => {
    if (!data)
      return {
        projectIndicators: [],
        projectTotalIndicators: [],
        hasData: [],
      };

    (async function () {
      let totalFinancementTemp = 0;
      let FSVs = 0;
      // const prjs = programs.data?.find((program) => program.id == id).projects;
      const prjs = projects;
      for (let i = 0; i < prjs.length; i++) {
        const project = prjs[i];
        let total = 0;
        await project.desiredFunding.forEach(async (dFunding: any) => {
          await convertToEuro(
            dFunding.amount,
            devises?.find((devise) => devise?.id === dFunding.devise.id)
          ).then((mt) => {
            totalFinancementTemp += mt;
            total += mt;
          });
        });
        await project.obtainedFunding.forEach(async (oFunding) => {
          await convertToEuro(
            oFunding.amount,
            devises?.find((devise) => devise?.id === oFunding.devise.id)
          ).then((mt) => (totalFinancementTemp += mt));
        });
        FSVs += project?.fsv === null ? total * 0.15 : project?.fsv;
      }
      setTotalFinancement(totalFinancementTemp + FSVs);
    })();

    return calculReportingProject(
      data,
      null,
      projectSelected,
      false,
      "programme"
    );
  }, [data, projectSelected, devises]);

  useEffect(() => {
    const fin = dataReportingV2?.projectTotalIndicators.filter(
      (ind) =>
        ind.indicator.projectOwnerReporting !== null &&
        ind.type === 1 &&
        ind.indicator.code === "euro_recus"
    );
    if (fin.length > 0) {
      setTotalSuivi(fin[0].total);
      if (fin[0].total !== totalFinancement) {
        setShowWarningFinancement(true);
      }
    }
  }, [dataReportingV2, totalFinancement]);
  // Effect to calculate contribution totals (obtenu & appel)
  useEffect(() => {
    if (projectSelected.length === 0) {
      setValueCO(0);
      setValueAC(0);
      return;
    }
    const filteredProjects = projects?.filter((p: any) =>
      projectSelected.some((sel) => sel.id === p.id)
    );

    const getCalculTotal = async () => {
      const { totalDesired, totalObtained } = await calculateFundingTotals(
        filteredProjects
      );
      setValueCO(totalObtained);
      setValueAC(totalDesired);
    };
    getCalculTotal();
  }, [projectSelected]);
  useEffect(() => {
    if (projects?.length > 0) {
      setProjectSelected(
        projects?.map((project) => ({
          name: project.name,
          id: project.id,
        }))
      );
    }
  }, [projects]);
  const dataReportingGraph = useMemo(() => {
    if (!data) return null;
    return calculReportingProject(
      data,
      null,
      projectSelected,
      false,
      "programme"
    );
  }, [data, projectSelected]);

  return (
    <div className="flex mt-6">
      <div className="w-3/6">
        <div className="flex flex-wrap items-start mr-[1rem] ml-[-0.65rem] sticky top-4">
          {projects?.map((project: any, key: number) => (
            <div
              className="w-full lg:w-1/2 xl:w-1/3 px-[0.65rem] min-w-[230px] mb-4"
              key={key}
            >
              <CardProject
                key={project.id}
                id={project.id}
                title={project.name}
                picture={project.cover ? project.cover.path : ""}
                type={project.type.id}
                link={`/${firstname}/project/${project.id}`}
                status={project.status}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="w-3/6 pl-6">
        <div className="sticky top-4">
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Modifier la sélection</SheetTitle>
                <SheetDescription>
                  Choisissez les projets que vous voulez additionner
                </SheetDescription>
              </SheetHeader>
              <div className="flex items-center space-x-2 border-b pb-2 mt-5">
                <Checkbox
                  checked={projectSelected.length === projects.length}
                  onCheckedChange={() =>
                    handleChangeProjectSelected({ name: "all", id: 0 })
                  }
                  id="all"
                />
                <label
                  htmlFor="all"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {projectSelected.length === projects.length
                    ? "Désélectionner tout"
                    : "Sélectionner tout"}
                </label>
              </div>
              <div
                className="mt-2 flex flex-col space-y-3 overflow-y-auto"
                style={{ maxHeight: "calc(100vh - 154px)" }}
              >
                {projects?.map((project: { name: string; id: number }) => (
                  <div key={project.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`terms-${project.id}`}
                      checked={isChecked({
                        name: project.name,
                        id: project.id,
                      })}
                      onCheckedChange={() =>
                        handleChangeProjectSelected({
                          name: project.name,
                          id: project.id,
                        })
                      }
                    />
                    <label
                      htmlFor={`terms-${project.id}`}
                      className="text-sm  leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {project.name}
                    </label>
                  </div>
                ))}
              </div>
            </SheetContent>
          </Sheet>
          <Sheet open={isSheetOpenInd} onOpenChange={setIsSheetOpenInd}>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Afficher les indicateurs</SheetTitle>
                <SheetDescription>
                  {/* mettre une description pour d'écrire qu'on peut géré facilement l'affichage d'indicateurs içi */}
                  Choisissez les indicateurs que vous voulez afficher
                </SheetDescription>
              </SheetHeader>

              <div
                className="mt-4 flex flex-col space-y-3 overflow-y-auto"
                style={{ maxHeight: "calc(100vh - 154px)" }}
              >
                {indicatorLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <div className="flex justify-between" key={i}>
                      <div>
                        <Skeleton className="w-28 h-5 mb-1" />
                        <div className="flex gap-2 mb-1">
                          <Skeleton className="w-10 h-3" />
                          <Skeleton className="w-10 h-3" />
                        </div>
                        <div className="flex gap-2">
                          <Skeleton className="w-10 h-3" />
                          <Skeleton className="w-10 h-3" />
                        </div>
                      </div>
                      <div>
                        <Skeleton className="w-10 h-3" />
                      </div>
                    </div>
                  ))}
                {!indicatorLoading &&
                  indicators?.filter(indicator => indicator.id !== 1)
                    .map((indicator) => (
                      <div
                        key={indicator.id}
                        className="flex items-center space-x-2"
                      >
                        <label
                          htmlFor={`terms-${indicator.id}`}
                          className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex flex-col border-b w-full pb-3"
                        >
                          <strong>
                            {/* {indicator.id == 1 ? "Coût total" : indicator.name} */}
                            {indicator.name}
                          </strong>
                          <div className="flex items-start justify-between mt-2">
                            <div className="flex  flex-col gap-1 opacity-65">
                              <div className="flex items-center">
                                Unité :{" "}
                                <Badge variant={"outline"} className="ml-2">
                                  {indicator.id == 1 ? "€" : indicator.unity}
                                </Badge>
                              </div>
                              <div className="flex items-center">
                                ESRS :{" "}
                                <Badge variant={"outline"} className="ml-2">
                                  {indicator.esrs}
                                </Badge>
                              </div>
                            </div>
                            <Switch
                              checked={indicator.programme}
                              onCheckedChange={(checked) => {
                                handleChangeParamsIndicator(indicator, checked);
                              }}
                              id={`terms-${indicator.id}`}
                            />
                          </div>
                        </label>
                      </div>
                    ))}
              </div>
            </SheetContent>
          </Sheet>
          <Tabs className=" w-full" defaultValue="objectif">
            <TabsList className="mb-2 grid w-full grid-cols-2 bg-background">
              <TabsTrigger
                value="objectif"
                className="rounded-none border-b-2 text-[#626262] data-[state=active]:border-foreground data-[state=active]:shadow-none"
              >
                Objectifs du programme
              </TabsTrigger>
              <TabsTrigger
                id="data-reporting"
                value="zone"
                className="rounded-none border-b-2 text-[#626262] data-[state=active]:border-foreground data-[state=active]:shadow-none"
              >
                Zone du programme
              </TabsTrigger>
            </TabsList>
            <TabsContent value="objectif" className="mb-4">
              <>
                <div className="mt-4 flex justify-end">
                  <Button
                    variant={"outline"}
                    onClick={() => {
                      setIsSheetOpen(true);
                    }}
                    className="edit-selection-btn"
                  >
                    {/* <Edit className="mr-3 size-5" /> */}
                    Modifier la sélection
                  </Button>
                  <Button
                    variant={"outline"}
                    onClick={() => setIsSheetOpenInd(true)}
                    className="edit-selection-btn rounded-s-none"
                  >
                    <Eye className="mr-3 size-5 text-muted-foreground" />
                    Afficher les indicateurs
                  </Button>
                </div>

                {(dataReportingV2?.projectTotalIndicators?.length ?? 0) > 0 && (
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <div
                        className="mt-4 rounded-[20px] bg-[#F7F9FB] p-[20px] h-[395px] overflow-y-auto"
                        id="project-cards"
                      >
                        {(dataReportingV2?.projectTotalIndicators?.filter(
                          (ind) =>
                            ind?.type === 1 &&
                            ind?.indicator?.programReporting !== null
                        ).length ?? 0) === 0 && (
                            <p className="m-0 text-left">
                              {t("Aucune donnée à afficher.")}
                            </p>
                          )}
                        {(dataReportingV2?.projectTotalIndicators?.filter(
                          (ind) =>
                            ind?.type === 1 &&
                            ind?.indicator?.programReporting !== null
                        ).length ?? 0) > 0 && (
                            <StatCardColoredProgram
                              showWarningFinancement={showWarningFinancement}
                              totalFinancement={totalFinancement}
                              totalSuivi={totalSuivi}
                              indicators={dataReportingV2?.projectTotalIndicators.filter(
                                (ind) => ind?.indicator?.programReporting !== null
                              )}
                              contributionObtenu={valueCO}
                              appelAContribution={valueAC}
                              context="programme"
                            />
                          )}
                      </div>
                    </ErrorBoundary>
                  </Suspense>
                )}

                <div className="flex flex-wrap mt-5" id="objectives-chart">
                  {(!data || !dataReportingGraph) && <LoadingSpinner />}

                  {data &&
                    dataReportingGraph &&
                    data?.indicators
                      .filter(
                        (i: any) =>
                          i.programReporting !== null &&
                          ![1].includes(i.id) &&
                          i?.id !== 1 &&
                          dataReportingGraph.hasData.includes(i.id)
                      )
                      .map((indicator: any, k: number) => (
                        <ChartItemGlobal
                          key={indicator.id ?? k}
                          dataReporting={dataReportingGraph.projectIndicators}
                          minDate={parseInt(data.minDate)}
                          maxDate={parseInt(data.maxDate)}
                          indicator={indicator}
                          currentYear={data.minDate}
                          width="w-full"
                          maxTco2Year={dataReportingGraph?.maxTco2Year}
                          defaultPreview="3"
                          context='programme'
                        />
                      ))}
                </div>
              </>
            </TabsContent>
            <TabsContent value="zone" className="mb-4">
              <div className="mt-5">
                <div className="my-4 flex justify-end">
                  <Button
                    variant={"outline"}
                    onClick={() => {
                      setIsSheetOpen(true);
                    }}
                    className="edit-selection-btn"
                  >
                    {/* <Edit className="mr-3 size-5" /> */}
                    Modifier la sélection
                  </Button>
                </div>
                <div className="overflow-hidden rounded-[20px]">
                  <MapClusterPrograms projectsSelected={projects} />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
export default ProjectListCard;
